import { resolveTenantId, ensureTenantSettingsRow, normalizeCompanySettingsError } from './companySettingsService';
import { supabase } from '../supabase';

const FIELD_ROLES = ['worker', 'supervisor'];

/**
 * RD-ROLE-005 — owner-only write of company_settings.price_visible_field_roles.
 * Exact shape of saveMfaRequiredRoles: session-derived tenant, the UPDATE is
 * double-scoped on `id` (from ensureTenantSettingsRow's row) AND `tenant_id`,
 * and reads back via `.single()` — not `.maybeSingle()` — so a write RLS
 * denies (non-owner, or a stale-isOwner tab after an ownership transfer)
 * comes back as 0-rows-updated PostgREST error PGRST116 instead of a silent
 * `{ data: null, error: null }`. The DB CHECK rejects any non-field role; we
 * filter client-side too so a stray 'admin' never reaches it.
 *
 * Audit (M2, RD-ROLE-005 security round): this used to also fire a client-side
 * writeAuditEvent, but company_settings UPDATE is an ordinary owner-gated RLS
 * policy — a direct PostgREST PATCH to the same column bypassed this function
 * entirely and left no audit_events row. The audit now lives server-side in
 * the audit_price_visibility_change trigger (20260909120300, AFTER UPDATE OF
 * price_visible_field_roles ON company_settings), which fires for every write
 * regardless of path. Do not re-add a client emit here — that would double-log
 * the writes that DO go through this function.
 * @param {string[]} roles
 * @param {string|null} [tenantId]
 * @returns {Promise<{ data: string[]|null, error: Error|null }>}
 */
export const savePriceVisibleFieldRoles = async (roles, tenantId = null) => {
  const next = FIELD_ROLES.filter((r) => (roles || []).includes(r));
  try {
    const resolvedTenantId = await resolveTenantId(tenantId);
    if (!resolvedTenantId) {
      return { data: null, error: new Error('No active tenant membership found for this account.') };
    }
    const { data: row, error: ensureError } = await ensureTenantSettingsRow(resolvedTenantId);
    if (ensureError) return { data: null, error: normalizeCompanySettingsError(ensureError) };

    const { data, error } = await supabase
      .from('company_settings')
      .update({ price_visible_field_roles: next })
      .eq('id', row.id)
      .eq('tenant_id', resolvedTenantId)
      .select('price_visible_field_roles')
      .single();

    return {
      data: data?.price_visible_field_roles ?? null,
      error: error ? normalizeCompanySettingsError(error) : null,
    };
  } catch (err) {
    return { data: null, error: normalizeCompanySettingsError(err) };
  }
};
